import { MouseEvent, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { RateResult, Service } from '@mews-commander/data-access';
import {
    FormRadioGroupField,
    LocalizedProperty,
    LocalizedText,
    useDialog,
    useLocalizeNumber,
    useLocalizeProperty,
    useLocalizeText,
} from '@mews/framework';
import { Button, IconName, RadioButton, Stack, Typography } from '@optimus-web/core';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { RatesOptionsNames } from '../../../../../schemas';
import { createRateGroupOptions } from '../../../../../utils';
import { RoutingRulesFormFields } from '../../../utils';
import { useDeletedItems } from '../../../hooks';
import { RatesDialog } from './RatesDialog';

export const RateSelectField = ({
    service,
    rates,
    dataTestId,
}: {
    service: Service;
    rates: RateResult;
    dataTestId: string;
}) => {
    const [{ onClose, active }, openDialog] = useDialog();
    const { watch, setValue } = useFormContext();
    const localizeNumber = useLocalizeNumber();
    const localizeProperty = useLocalizeProperty();
    const localizeText = useLocalizeText();
    const { deletedRatesCategory } = useDeletedItems(service.id);

    const selectedRates = watch(RoutingRulesFormFields.serviceSelectedRates(service.id));
    const selectedRatesCount = localizeNumber(selectedRates?.length ?? 0);
    const serviceRates = useMemo(
        () => rates.rates?.filter((rate) => rate.serviceId === service.id) ?? [],
        [rates.rates, service.id]
    );
    const totalRatesCount = localizeNumber(serviceRates?.length ?? 0);
    const selectedRatesNumberLabel = `${UnicodeEntity.LeftParenthesis}${selectedRatesCount}${UnicodeEntity.ForwardSlash}${totalRatesCount}${UnicodeEntity.RightParenthesis}`;

    const localizeNames = (names: { [key: string]: string }) => localizeProperty(names);
    const rateOptions = createRateGroupOptions({
        serviceId: service.id,
        rates,
        localizeNames,
        localizeText,
        deletedRatesCategory,
    });

    const onDialogOpen = (e: MouseEvent) => {
        e.preventDefault();
        openDialog();
    };

    useEffect(() => {
        if (watch(RoutingRulesFormFields.serviceRatesType(service.id)) == null) {
            setValue(RoutingRulesFormFields.serviceRatesType(service.id), RatesOptionsNames.All);
        }
    }, [setValue, service.id, watch]);

    useEffect(() => {
        if (watch(RoutingRulesFormFields.serviceSelectedRates(service.id)) == null) {
            setValue(
                RoutingRulesFormFields.serviceSelectedRates(service.id),
                serviceRates.map((rate) => rate.id)
            );
        }
    }, [service.id, serviceRates, setValue, watch]);

    return (
        <Stack data-test-id={dataTestId}>
            <FormRadioGroupField
                name={RoutingRulesFormFields.serviceRatesType(service.id)}
                label={<LocalizedProperty property={service.names} />}
            >
                <RadioButton
                    label={<LocalizedText textKey={TextKey.RoutingRulesFormAllRatesOption} />}
                    value={RatesOptionsNames.All}
                    key={RatesOptionsNames.All}
                />
                <RadioButton
                    label={
                        <Stack itemAlignment="center">
                            <LocalizedText textKey={TextKey.RoutingRulesFormSelectedRatesOption} />
                            {watch(RoutingRulesFormFields.serviceRatesType(service.id)) ===
                                RatesOptionsNames.Selected && (
                                <>
                                    {UnicodeEntity.NoBreakSpace}
                                    <Typography textStyle="bodyMedium" variant="secondary" as="div">
                                        {selectedRatesNumberLabel}
                                    </Typography>
                                    {UnicodeEntity.NoBreakSpace}
                                    <Button
                                        icon={IconName.Edit}
                                        onClick={onDialogOpen}
                                        size="small"
                                        type="submit"
                                        data-test-id={DataTestId.RoutingRulesRatesEditButton}
                                    >
                                        <LocalizedText textKey={TextKey.Edit} />
                                    </Button>
                                </>
                            )}
                        </Stack>
                    }
                    value={RatesOptionsNames.Selected}
                    key={RatesOptionsNames.Selected}
                />
            </FormRadioGroupField>
            <RatesDialog
                active={active}
                onClose={onClose}
                service={service}
                rateOptions={rateOptions}
                selectedRatesCount={selectedRatesCount}
                totalRatesCount={totalRatesCount}
            />
        </Stack>
    );
};
